import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CheckCircle, AlertTriangle, Package, User, Mail, Phone } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
export function ConfirmationModal({
  open,
  onOpenChange,
  formData,
  onConfirm
}) {
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const tableData = formData.tableData || [];
  const calculations = {
    totalItems: tableData.reduce((sum, row) => sum + (row.quantity || 0), 0),
    totalArea: tableData.reduce((sum, row) => sum + (row.totalArea || 0), 0),
    totalWeight: tableData.reduce((sum, row) => sum + (row.totalWeight || 0), 0),
    maxWeight: Math.max(...tableData.map(row => row.weightMT || 0), 0),
    maxLength: Math.max(...tableData.map(row => row.lengthM || 0), 0),
    maxWidth: Math.max(...tableData.map(row => row.widthM || 0), 0),
    maxHeight: Math.max(...tableData.map(row => row.heightM || 0), 0)
  };
  const handleConfirm = () => {
    // Store contact info in formData
    const updatedFormData = {
      ...formData,
      contactInfo
    };
    onConfirm(updatedFormData);
    onOpenChange(false);
  };
  const handleInputChange = e => {
    const {
      id,
      value
    } = e.target;
    setContactInfo(prev => ({
      ...prev,
      [id]: value
    }));
  };
  const hasData = tableData.length > 0 && tableData.some(row => row.rawWeight > 0 && row.rawLength > 0);
  return <Dialog open={open} onOpenChange={onOpenChange} data-id="element-1">
      <DialogContent className="sm:max-w-[650px]" data-id="element-2">
        <DialogHeader data-id="element-3">
          <DialogTitle className="text-xl flex items-center gap-2" data-id="element-4">
            {hasData ? <>
                <CheckCircle className="h-5 w-5 text-green-600" data-id="element-5" />
                Confirm Your Cargo Details
              </> : <>
                <AlertTriangle className="h-5 w-5 text-amber-500" data-id="element-6" />
                Incomplete Data
              </>}
          </DialogTitle>
          <DialogDescription data-id="element-7">
            {hasData ? 'Please review your cargo summary and provide your contact information.' : 'We need more information about your cargo to calculate barge requirements.'}
          </DialogDescription>
        </DialogHeader>
        {hasData ? <div className="py-4 space-y-6" data-id="element-8">
            {/* Reorganized Cargo Summary Table */}
            <div className="bg-white rounded-lg border" data-id="element-9">
              <Table data-id="element-10">
                <TableHeader data-id="element-11">
                  <TableRow data-id="element-12">
                    <TableHead className="bg-blue-100 text-black font-bold text-lg py-4" colSpan={4} data-id="element-13">
                      Cargo Summary
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-id="element-14">
                  <TableRow data-id="element-15">
                    <TableCell className="font-medium" colSpan={2} data-id="element-16">
                      Total Items
                    </TableCell>
                    <TableCell className="text-right" colSpan={2} data-id="element-17">
                      {calculations.totalItems}
                    </TableCell>
                  </TableRow>
                  <TableRow data-id="element-18">
                    <TableCell className="font-medium" data-id="element-19">Total Area</TableCell>
                    <TableCell className="text-right" data-id="element-20">
                      {calculations.totalArea.toFixed(2)} m²
                    </TableCell>
                    <TableCell className="font-medium" data-id="element-21">Total Weight</TableCell>
                    <TableCell className="text-right" data-id="element-22">
                      {calculations.totalWeight.toFixed(2)} mT
                    </TableCell>
                  </TableRow>
                  <TableRow data-id="element-23">
                    <TableCell className="font-medium" data-id="element-24">
                      Max Cargo Length
                    </TableCell>
                    <TableCell className="text-right" data-id="element-25">
                      {calculations.maxLength.toFixed(2)} m (=
                      {(calculations.maxLength / 0.3048).toFixed(2)}ft)
                    </TableCell>
                    <TableCell className="font-medium" data-id="element-26">Max Weight</TableCell>
                    <TableCell className="text-right" data-id="element-27">
                      {calculations.maxWeight.toFixed(2)} mT
                    </TableCell>
                  </TableRow>
                  <TableRow data-id="element-28">
                    <TableCell className="font-medium" data-id="element-29">
                      Max Cargo Width
                    </TableCell>
                    <TableCell className="text-right" data-id="element-30">
                      {calculations.maxWidth.toFixed(2)} m (=
                      {(calculations.maxWidth / 0.3048).toFixed(2)}ft)
                    </TableCell>
                    <TableCell className="font-medium" data-id="element-31">Max Height</TableCell>
                    <TableCell className="text-right" data-id="element-32">
                      {calculations.maxHeight.toFixed(2)} m (=
                      {(calculations.maxHeight / 0.3048).toFixed(2)}ft)
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
            {/* Contact Form */}
            <div className="bg-[#4183b8] text-white rounded-lg p-6" data-id="element-33">
              <h3 className="text-xl font-bold mb-4" data-id="element-34">Your Contact Details</h3>
              <div className="space-y-4" data-id="element-35">
                <div className="space-y-2" data-id="element-36">
                  <Label htmlFor="name" className="text-white" data-id="element-37">
                    Your Name
                  </Label>
                  <Input id="name" placeholder="John Doe" value={contactInfo.name} onChange={handleInputChange} className="bg-white text-black" data-id="element-38" />
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-39">
                  <div className="space-y-2" data-id="element-40">
                    <Label htmlFor="email" className="text-white" data-id="element-41">
                      Email Address
                    </Label>
                    <Input id="email" type="email" placeholder="john@company.com" value={contactInfo.email} onChange={handleInputChange} className="bg-white text-black" data-id="element-42" />
                  </div>
                  <div className="space-y-2" data-id="element-43">
                    <Label htmlFor="phone" className="text-white" data-id="element-44">
                      Phone Number
                    </Label>
                    <Input id="phone" placeholder="+1 (555) 000-0000" value={contactInfo.phone} onChange={handleInputChange} className="bg-white text-black" data-id="element-45" />
                  </div>
                </div>
              </div>
            </div>
          </div> : <div className="py-4" data-id="element-46">
            <div className="bg-amber-50 p-4 rounded-md border border-amber-200" data-id="element-47">
              <p className="text-amber-800" data-id="element-48">
                Please add at least one cargo item with weight and dimensions
                before calculating results.
              </p>
            </div>
          </div>}
        <DialogFooter data-id="element-49">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-id="element-50">
            Back to Input
          </Button>
          {hasData && <Button onClick={handleConfirm} className="bg-[#1a365d]" data-id="element-51">
              Confirm & View Results
            </Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}